import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Activity as ActIcon, CalendarRange, TrendingUp, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const ALL = "__all__";
const barColors = ["#FFD700", "#E30613", "#10B981", "#F59E0B", "#3B82F6", "#A855F7"];

export default function Analisi() {
  const [month, setMonth] = useState(ALL);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [activityType, setActivityType] = useState(ALL);
  const [presenceType, setPresenceType] = useState(ALL);
  const [activityTypes, setActivityTypes] = useState([]);
  const [presenceTypes, setPresenceTypes] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/activity-types").then((r) => setActivityTypes(r.data));
    api.get("/presence-types").then((r) => setPresenceTypes(r.data));
  }, []);

  const load = useCallback(() => {
    const params = {};
    if (month !== ALL) params.month = month;
    if (year !== ALL) params.year = year;
    if (activityType !== ALL) params.activity_type = activityType;
    if (presenceType !== ALL) params.presence_type = presenceType;
    api.get("/analysis", { params }).then((r) => setData(r.data));
  }, [month, year, activityType, presenceType]);

  useEffect(load, [load]);

  const exportExcel = async () => {
    try {
      const params = {};
      if (month !== ALL) params.month = month;
      if (year !== ALL) params.year = year;
      if (activityType !== ALL) params.activity_type = activityType;
      if (presenceType !== ALL) params.presence_type = presenceType;
      const res = await api.get("/analysis/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "analisi_presenze.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Report Excel scaricato");
    } catch (e) {
      toast.error("Errore nell'esportazione");
    }
  };

  const years = [];
  for (let y = new Date().getFullYear() + 1; y >= 2023; y--) years.push(String(y));

  const incidenceData = data
    ? data.activity_values.map((v) => ({
        name: v,
        periodo: data.incidence_period[v]?.percentage || 0,
        totale: data.incidence_total[v]?.percentage || 0,
        count: data.incidence_period[v]?.count || 0,
        avg: data.incidence_period[v]?.avg_participation || 0,
      }))
    : [];

  return (
    <div data-testid="analisi-page">
      <PageHeader title="Analisi Presenze" subtitle="Riepilogo e incidenza delle attività per periodo">
        <Button
          onClick={exportExcel}
          data-testid="export-excel-btn"
          className="bg-rugby-yellow text-black hover:bg-rugby-yellow/90 font-head uppercase tracking-wide font-bold"
        >
          <Download className="h-4 w-4 mr-2" /> Esporta Excel
        </Button>
      </PageHeader>

      {/* Filters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Mese", value: month, set: setMonth, testid: "filter-month", options: [{ v: ALL, l: "Tutti i mesi" }, ...MONTHS.map((m, i) => ({ v: String(i + 1), l: m }))] },
          { label: "Anno", value: year, set: setYear, testid: "filter-year", options: [{ v: ALL, l: "Tutti" }, ...years.map((y) => ({ v: y, l: y }))] },
          { label: "Tipo Attività", value: activityType, set: setActivityType, testid: "filter-activity", options: [{ v: ALL, l: "Tutte" }, ...activityTypes.map((t) => ({ v: t.value, l: t.value }))] },
          { label: "Tipo Presenza", value: presenceType, set: setPresenceType, testid: "filter-presence", options: [{ v: ALL, l: "Tutte" }, ...presenceTypes.map((t) => ({ v: t.value, l: t.value }))] },
        ].map((f) => (
          <div key={f.label} className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{f.label}</Label>
            <Select value={f.value} onValueChange={f.set}>
              <SelectTrigger data-testid={f.testid} className="bg-black/50 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {f.options.map((o) => (
                  <SelectItem key={o.v} value={o.v}>
                    {o.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Kpi icon={ActIcon} label="Attività (filtro)" value={data?.total_activities ?? "—"} accent="text-rugby-yellow" />
        <Kpi icon={CalendarRange} label="Attività nel periodo" value={data?.period_activities ?? "—"} accent="text-white" />
        <Kpi icon={TrendingUp} label="Attività totali" value={data?.all_time_activities ?? "—"} accent="text-white" />
      </div>

      {/* Incidence chart */}
      <div className="border border-border bg-card p-6 mb-8">
        <h3 className="font-head uppercase tracking-tight text-xl font-bold mb-1">Incidenza Tipo Attività</h3>
        <p className="text-xs text-muted-foreground mb-6">Percentuale di ogni tipo sul totale delle attività (periodo selezionato)</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={incidenceData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
              <YAxis tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                contentStyle={{ background: "#0b0b0b", border: "1px solid #27272A", borderRadius: 0, fontSize: 12 }}
                formatter={(val, name) => [`${val}%`, name === "periodo" ? "Periodo" : "Totale"]}
              />
              <Bar dataKey="periodo" radius={[0, 0, 0, 0]}>
                {incidenceData.map((e, i) => (
                  <Cell key={i} fill={barColors[i % barColors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {incidenceData.map((e, i) => (
            <div key={e.name} className="border border-border p-3" data-testid={`incidence-${e.name}`}>
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5" style={{ background: barColors[i % barColors.length] }} />
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{e.name}</span>
              </div>
              <div className="font-head font-bold text-2xl mt-1">{e.periodo}%</div>
              <div className="text-[10px] text-muted-foreground">Totale: {e.totale}% · {e.count} att.</div>
              <div className="mt-2 pt-2 border-t border-border">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Partecipazione media</div>
                <div className="font-head font-bold text-lg text-rugby-yellow leading-none mt-0.5">{e.avg}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per athlete summary */}
      <div className="border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-head uppercase tracking-tight text-xl font-bold">Riepilogo per Atleta</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="bg-[#0b0b0b] text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                <th className="text-left px-6 py-3">Atleta</th>
                {data?.presence_values.map((p) => (
                  <th key={p} className="text-center px-3 py-3">{p}</th>
                ))}
                <th className="text-center px-6 py-3">Totale</th>
                <th className="text-center px-6 py-3">Presenza %</th>
              </tr>
            </thead>
            <tbody>
              {data?.summary.map((s) => (
                <tr key={s.athlete_id} data-testid={`summary-row-${s.athlete_id}`} className="border-t border-border hover:bg-white/5">
                  <td className="px-6 py-3">
                    <span className="font-head font-semibold uppercase text-sm">{s.cognome}</span>{" "}
                    <span className="text-muted-foreground text-sm">{s.nome}</span>
                  </td>
                  {data.presence_values.map((p) => (
                    <td key={p} className="text-center px-3 py-3 font-mono text-sm">
                      {s.counts[p] ? <span className="text-white">{s.counts[p]}</span> : <span className="text-muted-foreground/40">0</span>}
                    </td>
                  ))}
                  <td className="text-center px-6 py-3 font-mono font-bold text-rugby-yellow">{s.total}</td>
                  <td className="text-center px-6 py-3">
                    <span className={`font-mono font-bold ${s.present_pct >= 75 ? "text-emerald-400" : s.present_pct >= 50 ? "text-amber-400" : "text-rugby-red"}`}>
                      {s.present_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-border bg-card p-5 flex items-center gap-4">
      <div className="h-11 w-11 bg-white/5 border border-border flex items-center justify-center">
        <Icon className={`h-5 w-5 ${accent}`} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
        <div className={`font-head font-bold text-3xl leading-none mt-1 ${accent}`}>{value}</div>
      </div>
    </div>
  );
}
